import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { normalizePhone } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Truck, Loader2 } from "lucide-react";

function Field({ label, children, required }) {
  return (
    <div>
      <Label className="label-caps text-[10px]">{label}{required && <span className="text-[hsl(var(--destructive))]"> *</span>}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

export default function GtzBookDialog({ open, onOpenChange, quote, onBooked }) {
  const [form, setForm] = useState({});
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    if (open && quote) {
      const today = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
      const qd = (quote.pickup_date || "").slice(0, 10);
      setForm({
        pickup_date: qd >= today ? qd : today,
        origin_company: quote.origin_company || "Premier Handling Solutions",
        origin_street: quote.origin_address1 || "",
        origin_contact_name: quote.origin_contact_name || "",
        origin_phone: quote.origin_contact_phone || "",
        origin_ready_time: "09:00",
        origin_close_time: "17:00",
        dest_company: quote.dest_company || quote.customer_name || "",
        dest_street: quote.dest_address1 || "",
        dest_contact_name: quote.dest_contact_name || "",
        dest_phone: quote.dest_contact_phone || "",
        dest_ready_time: "09:00",
        dest_close_time: "17:00",
        shipper_ref: quote.quote_ref || "",
        po_number: "",
        pickup_remarks: "",
        delivery_remarks: "",
      });
    }
  }, [open, quote]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Flag a non-empty phone that isn't a valid 10-digit US number (empty is fine — it's optional).
  const originPhoneBad = !!form.origin_phone?.trim() && !normalizePhone(form.origin_phone);
  const destPhoneBad = !!form.dest_phone?.trim() && !normalizePhone(form.dest_phone);

  const book = async () => {
    if (!form.origin_street?.trim() || !form.dest_street?.trim())
      return toast.error("Origin and destination street addresses are required by GlobalTranz.");
    if (!form.pickup_date)
      return toast.error("Choose a pickup date.");
    if (originPhoneBad || destPhoneBad)
      return toast.error("Enter valid 10-digit US phone numbers (or leave them blank).");
    setBooking(true);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/gtz-book`, form);
      toast.success(`Booked via GlobalTranz — BOL ${data.bol_number} (${data.carrier}).`);
      if (data.price_changed)
        toast.warning(`Note: carrier cost changed from $${data.price_changed.old_cost} to $${data.price_changed.new_cost} at booking.`);
      onOpenChange(false);
      onBooked?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBooking(false);
    }
  };

  if (!quote) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="gtz-book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <Truck className="h-5 w-5 text-[hsl(var(--primary))]" /> Book via GlobalTranz — {quote.quote_number}
          </DialogTitle>
          <DialogDescription>
            Books the selected &quot;via GlobalTranz&quot; rate as a real shipment and returns a GTZ BOL number.
            If the stored quote id expired, a fresh rate is pulled automatically for the same carrier.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-md border border-[hsl(var(--primary))]/30 bg-[hsl(var(--primary))]/5 p-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Pickup Date" required>
                <Input type="date" data-testid="gtz-pickup-date" value={form.pickup_date || ""} min={new Date().toISOString().slice(0, 10)} onChange={(e) => set("pickup_date", e.target.value)} className="h-9" />
              </Field>
              <p className="self-end pb-1 text-[11px] leading-snug text-muted-foreground">
                {(form.pickup_date || "") !== (quote.pickup_date || "").slice(0, 10)
                  ? "Different from the quoted date — GlobalTranz will re-rate for this date at booking."
                  : "Same date the quote was rated for."}
              </p>
            </div>
          </div>
          <div>
            <div className="label-caps mb-2 text-[hsl(var(--primary))]">Pickup (Origin · {quote.origin_zip})</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company"><Input data-testid="gtz-origin-company" value={form.origin_company || ""} onChange={(e) => set("origin_company", e.target.value)} className="h-9" /></Field>
              <Field label="Street Address" required><Input data-testid="gtz-origin-street" value={form.origin_street || ""} onChange={(e) => set("origin_street", e.target.value)} className="h-9" /></Field>
              <Field label="Contact"><Input value={form.origin_contact_name || ""} onChange={(e) => set("origin_contact_name", e.target.value)} className="h-9" /></Field>
              <Field label="Phone">
                <Input data-testid="gtz-origin-phone" value={form.origin_phone || ""} onChange={(e) => set("origin_phone", e.target.value)}
                  className={`h-9 ${originPhoneBad ? "border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]" : ""}`} />
                {originPhoneBad && <p className="mt-1 text-[11px] font-medium text-[hsl(var(--destructive))]" data-testid="gtz-origin-phone-error">Not a valid 10-digit US phone.</p>}
              </Field>
              <Field label="Ready Time"><Input type="time" value={form.origin_ready_time || ""} onChange={(e) => set("origin_ready_time", e.target.value)} className="h-9" /></Field>
              <Field label="Close Time"><Input type="time" value={form.origin_close_time || ""} onChange={(e) => set("origin_close_time", e.target.value)} className="h-9" /></Field>
            </div>
          </div>
          <div>
            <div className="label-caps mb-2 text-[hsl(var(--primary))]">Delivery (Destination · {quote.destination_zip})</div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company"><Input data-testid="gtz-dest-company" value={form.dest_company || ""} onChange={(e) => set("dest_company", e.target.value)} className="h-9" /></Field>
              <Field label="Street Address" required><Input data-testid="gtz-dest-street" value={form.dest_street || ""} onChange={(e) => set("dest_street", e.target.value)} className="h-9" /></Field>
              <Field label="Contact"><Input value={form.dest_contact_name || ""} onChange={(e) => set("dest_contact_name", e.target.value)} className="h-9" /></Field>
              <Field label="Phone">
                <Input data-testid="gtz-dest-phone" value={form.dest_phone || ""} onChange={(e) => set("dest_phone", e.target.value)}
                  className={`h-9 ${destPhoneBad ? "border-[hsl(var(--destructive))] focus-visible:ring-[hsl(var(--destructive))]" : ""}`} />
                {destPhoneBad && <p className="mt-1 text-[11px] font-medium text-[hsl(var(--destructive))]" data-testid="gtz-dest-phone-error">Not a valid 10-digit US phone.</p>}
              </Field>
              <Field label="Ready Time"><Input type="time" value={form.dest_ready_time || ""} onChange={(e) => set("dest_ready_time", e.target.value)} className="h-9" /></Field>
              <Field label="Close Time"><Input type="time" value={form.dest_close_time || ""} onChange={(e) => set("dest_close_time", e.target.value)} className="h-9" /></Field>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Shipper Ref #"><Input value={form.shipper_ref || ""} onChange={(e) => set("shipper_ref", e.target.value)} className="h-9" /></Field>
            <Field label="PO #"><Input value={form.po_number || ""} onChange={(e) => set("po_number", e.target.value)} className="h-9" /></Field>
            <Field label="Pickup Remarks"><Textarea rows={2} value={form.pickup_remarks || ""} onChange={(e) => set("pickup_remarks", e.target.value)} /></Field>
            <Field label="Delivery Remarks"><Textarea rows={2} value={form.delivery_remarks || ""} onChange={(e) => set("delivery_remarks", e.target.value)} /></Field>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="gtz-book-submit" onClick={book} disabled={booking || originPhoneBad || destPhoneBad} className="gap-1.5">
            {booking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />} Book Shipment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
